import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Minus, Plus, Loader2, RefreshCw } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReorderPage = () => {
  const { orderId } = useParams();
  const { customer, token, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) return;
    if (!customer || !token) { navigate('/portal/login'); return; }
    fetchOrderItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, authLoading, customer, token]);

  const fetchOrderItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reorder/orders/${orderId}/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(res.data);
      const initQty = {};
      res.data.items.forEach(item => { initQty[item.id] = item.original_quantity; });
      setQuantities(initQty);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const updateQty = (itemId, delta) => {
    setQuantities(prev => {
      const next = Math.max(1, (prev[itemId] || 1) + delta);
      return { ...prev, [itemId]: next };
    });
  };

  const subtotal = orderData
    ? orderData.items.reduce((sum, item) => sum + item.unit_price * (quantities[item.id] || 0), 0)
    : 0;
  const tax = subtotal * 0.06;
  const total = subtotal + tax;

  const handleCheckout = async () => {
    setSubmitting(true);
    setError('');
    try {
      // Step 1: Create the reorder
      const reorderRes = await axios.post(
        `${API_URL}/api/reorder`,
        {
          original_order_id: orderId,
          items: orderData.items.map(item => ({
            original_item_id: item.id,
            quantity: quantities[item.id] || item.original_quantity,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, order_number } = reorderRes.data;

      // Step 2: Create Stripe checkout session
      const checkoutRes = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { order_id, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Step 3: Redirect to Stripe
      window.location.href = checkoutRes.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Checkout failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#4A90A4] animate-spin" />
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-red-600">{error || 'Order not found'}</p>
        <button onClick={() => navigate('/portal/dashboard')} className="text-[#4A90A4] hover:underline text-sm">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="reorder-page">
      {/* Header */}
      <div className="bg-[#1B3A5C] py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <button onClick={() => navigate('/portal/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-3 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <RefreshCw className="w-6 h-6 text-[#F5A623]" />
            <div>
              <h1 className="text-xl font-bold text-white">Reorder from #{orderData.order_number}</h1>
              <p className="text-white/50 text-sm">
                Same items, same pricing — just adjust quantities
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm" data-testid="reorder-error">
            {error}
          </div>
        )}

        {/* Order Details */}
        {(orderData.school_name || orderData.decoration_method || orderData.garment_source) && (
          <div className="bg-white rounded-xl p-5 shadow-sm mb-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wider">Order Details (carried over)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              {orderData.school_name && (
                <div><span className="text-gray-400">School</span><p className="font-medium text-gray-900">{orderData.school_name}</p></div>
              )}
              {orderData.decoration_method && (
                <div><span className="text-gray-400">Method</span><p className="font-medium text-gray-900">{orderData.decoration_method}</p></div>
              )}
              {orderData.garment_source && (
                <div><span className="text-gray-400">Garment</span><p className="font-medium text-gray-900">{orderData.garment_source}</p></div>
              )}
              {orderData.business_name && (
                <div><span className="text-gray-400">Business</span><p className="font-medium text-gray-900">{orderData.business_name}</p></div>
              )}
              <div><span className="text-gray-400">Design</span><p className="font-medium text-[#4A90A4]">Repeat</p></div>
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="px-5 py-4 border-b border-gray-50">
            <h2 className="font-bold text-[#1B3A5C]">Select Items & Quantities</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {orderData.items.map(item => (
              <div key={item.id} className="px-5 py-4 flex items-center justify-between" data-testid={`reorder-item-${item.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <p className="font-medium text-gray-900 text-sm">{item.product_name}</p>
                  {item.variant_name && <p className="text-xs text-gray-400">{item.variant_name}</p>}
                  {item.customization_text && <p className="text-xs text-gray-400 italic mt-0.5">{item.customization_text}</p>}
                  <p className="text-sm text-[#4A90A4] font-semibold mt-1">${item.unit_price.toFixed(2)} each</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => updateQty(item.id, -1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    data-testid={`qty-minus-${item.id}`}
                  >
                    <Minus className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                  <span className="w-10 text-center font-bold text-gray-900" data-testid={`qty-value-${item.id}`}>
                    {quantities[item.id] || 0}
                  </span>
                  <button
                    onClick={() => updateQty(item.id, 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    data-testid={`qty-plus-${item.id}`}
                  >
                    <Plus className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                  <span className="w-20 text-right font-semibold text-gray-900 text-sm">
                    ${(item.unit_price * (quantities[item.id] || 0)).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary & Checkout */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="space-y-2 text-sm mb-5">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between text-gray-500"><span>Tax (6%)</span><span>${tax.toFixed(2)}</span></div>
            <div className="flex justify-between font-bold text-[#1B3A5C] text-base pt-2 border-t border-gray-100">
              <span>Total</span>
              <span data-testid="reorder-total">${total.toFixed(2)}</span>
            </div>
          </div>
          <button
            onClick={handleCheckout}
            disabled={submitting || total <= 0}
            className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F5A623] text-white font-bold rounded-xl hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            data-testid="checkout-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : (
              <><ShoppingCart className="w-4 h-4" /> Proceed to Checkout — ${total.toFixed(2)}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReorderPage;
